import { getRegistrationType } from '@accountFederation/modules/registration/registrationType';
import {
    RegistrationRequestIdError,
    RequestError,
    ValidationError,
} from '@superbet-group/web.lib.utilities';
import { IdentityProvider } from '@superbet-group/web.types';
import {
    AnalyticsEventTypes,
    AnalyticsProtoEvents,
} from './analyticsScreenNames';

export function createLanderAnalyticsPayload() {
    return {
        AnalyticsEvent: AnalyticsProtoEvents.contentLanderTransition,
        eventType: AnalyticsEventTypes.contentLanderTransition,
        [AnalyticsProtoEvents.contentLanderTransition]: {
            contentLanderHash: {
                value: retrieveLanderCookie(),
            },
        },
    };
}

enum RegistrationResult {
    REGISTRATION_RESULT_UNSPECIFIED = 0,
    REGISTRATION_RESULT_SUCCESS = 1,
    REGISTRATION_RESULT_FAIL = 2,
}

enum RegistrationType {
    REGISTRATION_TYPE_UNSPECIFIED = 0,
    REGISTRATION_TYPE_STANDARD = 1,
    REGISTRATION_TYPE_MOJEID = 2,
    REGISTRATION_TYPE_ITS_ME = 3,
}

type RegistrationResultOpts = {
    error?: unknown;
    playerCode?: string;
};

function getPayloadRegistrationType(identityProvider: string): RegistrationType {
    switch (identityProvider) {
        case IdentityProvider.MojeId:
            return RegistrationType.REGISTRATION_TYPE_MOJEID;
        case IdentityProvider.ItsMe:
            return RegistrationType.REGISTRATION_TYPE_ITS_ME;
        default:
            return RegistrationType.REGISTRATION_TYPE_STANDARD;
    }
}

const COOKIE_NAME = 'slp_hash';

export function retrieveLanderCookie(shouldDeleteCookie = false): string {
    const slpHashCookie = document.cookie.split(';').find((cookie) => cookie.trim().startsWith(`${COOKIE_NAME}=`));

    if (!slpHashCookie) {
        return '';
    }

    const slpHash = slpHashCookie.split('=')[1];

    if (shouldDeleteCookie) {
        deleteLanderCookie();
    }
    return slpHash || '';
}

function deleteLanderCookie() {
    const domainParts = window.location.hostname.split('.');
    const mainDomain = domainParts.slice(-2).join('.');
    document.cookie = `${COOKIE_NAME}=; domain=${mainDomain}; path=/; max-age=0;`;
}

function extractErrorMessage(error: unknown): string {
    const defaultError = 'unknown';
    try {
        if (error instanceof ValidationError) {
            return Object.values(error.fieldErrors).map((errArray) => errArray.join(' ')).join(' ');
        } else if (error instanceof RegistrationRequestIdError) {
            return `${error.message} | ${error.errorCode}`;
        } else if (error instanceof Error) {
            return error.message || defaultError;
        } else if (error instanceof RequestError) {
            return error.error.message || error.message || defaultError;
        } else {
            return defaultError;
        }
    } catch (e) {
        return defaultError;
    }
}

export function createLanderRegistrationResultAnalyticsPayload(result: RegistrationResultOpts) {
    const hasError = result.error || !result.playerCode;
    return {
        AnalyticsEvent: AnalyticsProtoEvents.registrationRequestResult,
        eventType: AnalyticsEventTypes.registrationRequestResult,
        [AnalyticsProtoEvents.registrationRequestResult]: {
            registrationResult: hasError ? RegistrationResult.REGISTRATION_RESULT_FAIL : RegistrationResult.REGISTRATION_RESULT_SUCCESS,
            registrationType: getPayloadRegistrationType(getRegistrationType()),
            registrationPlayerCode: {
                value: result.playerCode?.toString() || '',
            },
            failReason: {
                value: hasError ? extractErrorMessage(result.error) : '',
            },
            contentLanderHash: {
                value: retrieveLanderCookie(!hasError),
            },
        },
    };
}
